import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { api } from '@/lib/api';
import type {
  LessonPackInput,
  LessonPackStatus,
} from '@/lib/schema.types';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Pre-fills the form using whatever the parent page collected. */
  defaults: LessonPackInput;
  /** Called once generation reaches `done`. Parent should redirect to the
   *  pack detail/edit page. */
  onComplete: (packId: string) => void;
}

type Phase = 'idle' | 'submitting' | 'polling' | 'done' | 'error';

const POLL_INTERVAL_MS = 2500;

export function GenerateLessonPackDialog({
  open,
  onClose,
  defaults,
  onComplete,
}: Props) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [packId, setPackId] = useState<string | null>(null);
  const [status, setStatus] = useState<LessonPackStatus | null>(null);
  const [statusTitle, setStatusTitle] = useState<string | null>(null);
  const [estimatedMinutes, setEstimatedMinutes] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);

  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const tickTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAt = useRef<number>(0);

  // Reset state every time the dialog reopens so a previous run doesn't leak.
  useEffect(() => {
    if (!open) return;
    setPhase('idle');
    setPackId(null);
    setStatus(null);
    setStatusTitle(null);
    setEstimatedMinutes(null);
    setError(null);
    setElapsed(0);
  }, [open]);

  // Tear down timers when the dialog closes or unmounts.
  useEffect(() => {
    return () => stopTimers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!open) stopTimers();
  }, [open]);

  const stopTimers = () => {
    if (pollTimer.current) clearInterval(pollTimer.current);
    if (tickTimer.current) clearInterval(tickTimer.current);
    pollTimer.current = null;
    tickTimer.current = null;
  };

  const beginPolling = (id: string) => {
    startedAt.current = Date.now();
    tickTimer.current = setInterval(() => {
      setElapsed(Math.round((Date.now() - startedAt.current) / 1000));
    }, 1000);
    pollTimer.current = setInterval(async () => {
      try {
        const res = await api.lessonPacks.status(id);
        setStatus(res.status);
        // Ignore the pre-`done` placeholders (schema defaults "Untitled lesson"
        // / 0) so we only surface the real title + estimate once generation
        // has actually written them.
        if (res.title && res.title.toLowerCase() !== 'untitled lesson') {
          setStatusTitle(res.title);
        }
        if (
          typeof res.estimatedTotalMinutes === 'number' &&
          res.estimatedTotalMinutes > 0
        ) {
          setEstimatedMinutes(res.estimatedTotalMinutes);
        }
        if (res.status === 'done') {
          stopTimers();
          setPhase('done');
        } else if (res.status === 'error') {
          stopTimers();
          setPhase('error');
          setError(res.error ?? 'Generation failed.');
        }
      } catch (err) {
        stopTimers();
        setPhase('error');
        setError(err instanceof Error ? err.message : 'Polling failed.');
      }
    }, POLL_INTERVAL_MS);
  };

  const handleGenerate = async () => {
    setPhase('submitting');
    setError(null);
    try {
      const res = await api.lessonPacks.generate(defaults);
      setPackId(res.id);
      setStatus(res.status);
      setPhase('polling');
      beginPolling(res.id);
    } catch (err) {
      setPhase('error');
      setError(err instanceof Error ? err.message : 'Could not start generation.');
    }
  };

  const runDisabled = phase === 'submitting' || phase === 'polling';

  const footer = (() => {
    if (phase === 'idle') {
      return (
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleGenerate}>
            <Sparkles size={14} /> Generate lesson pack
          </Button>
        </>
      );
    }
    if (phase === 'done') {
      return (
        <>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => packId && onComplete(packId)}
            disabled={!packId}
          >
            Open lesson pack
          </Button>
        </>
      );
    }
    if (phase === 'error') {
      return (
        <>
          {packId && (
            <Button variant="secondary" onClick={() => onComplete(packId)}>
              Open partial pack
            </Button>
          )}
          <Button variant="primary" onClick={handleGenerate}>
            Try again
          </Button>
        </>
      );
    }
    // submitting / polling
    return (
      <Button variant="ghost" onClick={onClose}>
        Run in background
      </Button>
    );
  })();

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!runDisabled) onClose();
      }}
      title="Generate lesson pack with AI"
      description="The Lesson Pack runs generation asynchronously — once it's done, you can preview, edit, then publish."
      width="lg"
      footer={footer}
    >
      {phase === 'idle' && <ConfigSummary input={defaults} />}
      {phase !== 'idle' && (
        <ProgressView
          phase={phase}
          status={status}
          title={statusTitle}
          estimatedMinutes={estimatedMinutes}
          elapsed={elapsed}
          error={error}
        />
      )}
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */

function ConfigSummary({ input }: { input: LessonPackInput }) {
  const fields: Array<[string, string | undefined]> = [
    ['Board', input.board],
    ['Grade', input.grade],
    ['Subject', input.subject],
    ['Chapter', input.chapter],
    [
      input.topics.length > 1 ? 'Topics' : 'Topic',
      input.topics.length ? input.topics.join(', ') : input.topic,
    ],
    ['Scope', input.lessonScope],
    ['Engine', input.pipelineVersion],
  ];
  return (
    <div className="space-y-4">
      <p className="text-[13px] text-fg-muted">
        Claude will plan the beats, then expand them into editable slides. This
        usually takes 30–90 seconds — you can leave this dialog open or run it
        in the background.
      </p>
      <div className="rounded-md border border-border bg-bg-subtle/60 px-4 py-3">
        <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-fg-subtle">
          Inputs
        </div>
        <dl className="grid grid-cols-[110px_1fr] gap-y-1.5 text-[12.5px]">
          {fields
            .filter(([, v]) => v && v.trim?.())
            .map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="text-fg-subtle">{k}</dt>
                <dd className="text-fg">{v}</dd>
              </div>
            ))}
        </dl>
        {input.referenceText && (
          <details className="mt-3">
            <summary className="cursor-pointer text-[11.5px] font-semibold text-fg-muted">
              Reference text ({input.referenceText.length} chars)
            </summary>
            <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap rounded border border-border-subtle bg-white p-2 text-[11.5px] text-fg-muted">
              {input.referenceText}
            </pre>
          </details>
        )}
        {input.additionalNotes && (
          <p className="mt-3 text-[11.5px] text-fg-muted">
            <span className="font-semibold text-fg-subtle">Notes:</span>{' '}
            {input.additionalNotes}
          </p>
        )}
      </div>
    </div>
  );
}

function ProgressView({
  phase,
  status,
  title,
  estimatedMinutes,
  elapsed,
  error,
}: {
  phase: Phase;
  status: LessonPackStatus | null;
  title: string | null;
  estimatedMinutes: number | null;
  elapsed: number;
  error: string | null;
}) {
  const headline = (() => {
    if (phase === 'submitting') return 'Starting generation…';
    if (phase === 'polling') {
      if (status === 'pending') return 'Queued for generation…';
      if (status === 'generating') return 'Generating lesson pack…';
      return 'Working…';
    }
    if (phase === 'done') return 'Lesson pack ready';
    if (phase === 'error') return 'Generation failed';
    return '';
  })();

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {phase === 'done' ? (
          <CheckCircle2 size={16} className="text-emerald-500" />
        ) : phase === 'error' ? (
          <AlertTriangle size={16} className="text-red-500" />
        ) : (
          <Loader2 size={16} className="animate-spin text-accent" />
        )}
        <span className="text-sm font-semibold text-fg">{headline}</span>
        {phase === 'polling' && (
          <span className="ml-auto text-[11.5px] text-fg-subtle">
            {formatElapsed(elapsed)}
          </span>
        )}
      </div>

      {(title || estimatedMinutes) && (
        <div className="rounded-md border border-border bg-bg-subtle/40 px-3 py-2 text-[12.5px] text-fg-muted">
          {title && (
            <div>
              <span className="text-fg-subtle">Title:</span>{' '}
              <span className="text-fg">{title}</span>
            </div>
          )}
          {typeof estimatedMinutes === 'number' && (
            <div>
              <span className="text-fg-subtle">Estimated total:</span>{' '}
              <span className="text-fg">{estimatedMinutes} min</span>
            </div>
          )}
        </div>
      )}

      {phase === 'polling' && (
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-border-subtle">
          <div className="h-full animate-pulse bg-accent" style={{ width: '100%' }} />
        </div>
      )}

      {phase === 'error' && error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">
          {error}
        </div>
      )}
    </div>
  );
}

function formatElapsed(seconds: number) {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}
